import React from "react";
import line5 from "../../assets/images/svg/line-5.svg";

type AudienceTab = "artists" | "curators";
type ActiveNavItem = "newCampaign" | "myCampaigns" | "smartLinks";

type MetaAdsHeaderNavProps = {
  title?: string;
  activeAudienceTab: AudienceTab;
  onAudienceTabChange?: (tab: AudienceTab) => void;
  showAudienceToggle?: boolean;
  showNav?: boolean;
  activeNavItem?: ActiveNavItem;
  onNewCampaignClick?: () => void;
  onMyCampaignsClick?: () => void;
  onSmartLinksClick?: () => void;
  headerClassName?: string;
  description?: React.ReactNode;
};

const navItemClassName =
  "relative min-w-0 font-medium text-[#4b4f55] text-sm sm:text-base md:text-lg tracking-[0] leading-[20px] md:leading-[23.4px] hover:text-white transition-colors cursor-pointer text-left";

const activeNavItemClassName =
  "relative min-w-0 font-medium text-white text-sm sm:text-base md:text-lg tracking-[0] leading-[20px] md:leading-[23.4px]";

export default function MetaAdsHeaderNav({
  title = "Meta Ads Builder",
  activeAudienceTab,
  onAudienceTabChange,
  showAudienceToggle = true,
  showNav = true,
  activeNavItem = "newCampaign",
  onNewCampaignClick,
  onMyCampaignsClick,
  onSmartLinksClick,
  headerClassName = "flex flex-col w-full items-start gap-5 md:gap-6",
  description,
}: MetaAdsHeaderNavProps) {
  return (
    <div className="flex flex-col gap-[32px] w-full items-start">
      <header className={headerClassName}>
        <h1 className="w-full lg:font-bold font-semibold bg-black text-white text-[24px] lg:text-[36px]">
          {title}
        </h1>

        {showAudienceToggle && (
          <div
            className="rounded-[999px] lg:p-[1px] p-[1px] flex h-[48px] w-full lg:w-[350px]"
            style={{
              background:
                activeAudienceTab === "artists"
                  ? "linear-gradient(to right, #00CFFF00, white)"
                  : "linear-gradient(to right, white, #00CFFF00)",
            }}
          >
            <div
              className="inline-flex h-full w-full items-center lg:gap-4 px-[6px] lg:px-[5px] relative rounded-[999px]"
              style={{
                background:
                  activeAudienceTab === "artists"
                    ? "linear-gradient(to right, #2BFAA3, #1B76FF)"
                    : "linear-gradient(to right, #1B76FF, #2BFAA3)",
              }}
            >
              <button
                type="button"
                className={`flex-1 h-[38px] py-1 px-5 rounded-[999px] flex items-center justify-center transition-all text-[18px] font-medium ${
                  activeAudienceTab === "artists"
                    ? "bg-white text-[#020202]"
                    : "bg-transparent text-white"
                }`}
                onClick={() => onAudienceTabChange?.("artists")}
                aria-pressed={activeAudienceTab === "artists"}
              >
                <span className="font-medium text-[18px]">For artists</span>
              </button>
              <button
                type="button"
                className={`flex-1 h-[38px] py-1 px-5 rounded-[999px] flex items-center justify-center transition-all text-[18px] font-medium ${
                  activeAudienceTab === "curators"
                    ? "bg-white text-[#020202]"
                    : "bg-transparent text-white"
                }`}
                onClick={() => onAudienceTabChange?.("curators")}
                aria-pressed={activeAudienceTab === "curators"}
              >
                <span className="font-medium text-[18px]">For curators</span>
              </button>
            </div>
          </div>
        )}

        <img
          className="w-full max-w-[615px] h-px object-cover"
          alt=""
          src={line5}
        />

        <p className="w-full font-semibold text-white text-[16px] lg:text-[22px] line-height-[22px]">
          {description ?? (
            <>
              Create Meta ads easily and grow your{" "}
              {activeAudienceTab === "artists" ? "tracks" : "playlists"} with a
              proven music industry method!
            </>
          )}
        </p>
      </header>

      {showNav && (
        <nav className="flex flex-wrap items-center justify-between max-w-[480px] md:gap-2 pt-0 pb-4 px-0 w-full">
          <div className="flex h-[30px] min-w-0 items-center gap-1 relative">
            {activeNavItem === "newCampaign" ? (
              <span className={activeNavItemClassName}>New Campaign</span>
            ) : (
              <button className={navItemClassName} onClick={onNewCampaignClick}>
                New Campaign
              </button>
            )}
          </div>

          <div
            className="w-px h-[30px] flex-shrink-0 bg-[#202027]"
            aria-hidden
          />

          <div className="flex h-[30px] min-w-0 items-center gap-[33px] relative">
            <div className="flex min-w-0 items-center gap-[5px] relative">
              {activeNavItem === "myCampaigns" ? (
                <span className={activeNavItemClassName}>My Campaigns</span>
              ) : (
                <button
                  className={navItemClassName}
                  onClick={onMyCampaignsClick}
                >
                  My Campaigns
                </button>
              )}
            </div>
          </div>

          <div
            className="w-px h-[30px] flex-shrink-0 bg-[#202027]"
            aria-hidden
          />

          <div className="flex h-[30px] min-w-0 items-center gap-[33px] relative">
            <div className="flex min-w-0 items-center gap-[5px] relative">
              {activeNavItem === "smartLinks" ? (
                <span className={activeNavItemClassName}>Smart Links</span>
              ) : (
                <button
                  className={navItemClassName}
                  onClick={onSmartLinksClick}
                >
                  Smart Links
                </button>
              )}
            </div>
          </div>
        </nav>
      )}
    </div>
  );
}

export type { AudienceTab, ActiveNavItem, MetaAdsHeaderNavProps };

